import React, { lazy } from 'react';

/**
 * Wrapper around React.lazy that survives deployments.
 *
 * The "stale chunk" problem: chunk filenames are content-hashed and the old
 * ones are deleted on deploy, so a browser holding a pre-deploy index.html
 * requests chunks that 404 ("Importing a module script failed" on Safari).
 *
 * Recovery, in order:
 *  1. retry the import once (transient network blips),
 *  2. force ONE full reload per session — that refetches index.html (served
 *     with Cache-Control: no-cache) and with it the current chunk names.
 * The sessionStorage guard prevents a reload loop when the failure is real
 * (offline, genuinely broken deploy): the second failure after a reload
 * rethrows to the nearest error boundary instead.
 */
const RELOAD_GUARD_KEY = 'lazy-retry-reloaded';

export function lazyRetry<T extends React.ComponentType<any>>(importFn: () => Promise<{ default: T }>): React.LazyExoticComponent<T> {
  return lazy(() =>
    importFn().catch(() =>
      importFn().catch((err) => {
        let alreadyReloaded = false;
        try {
          alreadyReloaded = sessionStorage.getItem(RELOAD_GUARD_KEY) === 'true';
          if (!alreadyReloaded) sessionStorage.setItem(RELOAD_GUARD_KEY, 'true');
        } catch {
          // Storage unavailable (private mode) — fall through to rethrow.
          alreadyReloaded = true;
        }
        if (!alreadyReloaded) {
          window.location.reload();
          // Unresolved forever: the reload replaces the document.
          return new Promise<never>(() => {});
        }
        throw err;
      })
    )
  );
}
